import { useState, useEffect } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  getRequestById,
  getCredentialReqMinisters,
} from "@/data/credentialReqRepository";
import { getDistrictById, getRegionById } from "@/Services/regions.service";
import { canEditRequest } from "@/models/credentialRequest";
import type { CredentialRequest, Minister, RequestUrlParams } from "../types";

interface UseCredentialRequestReturn {
  // Data
  request: CredentialRequest | null;
  ministers: Minister[];
  districtName: string;
  regionName: string;
  urlParams: RequestUrlParams;
  isInstitutionRequest: boolean;
  isFunctionRequest: boolean;
  isNacionalFunctionRequest: boolean;
  
  // State
  isLoading: boolean;
  error: string | null;
  isNewRequest: boolean;
  canEdit: boolean;
  
  // Actions
  setRequest: (request: CredentialRequest | null) => void;
  setMinisters: (ministers: Minister[]) => void;
  setError: (error: string | null) => void;
  reloadRequest: () => Promise<void>;
}

/**
 * Hook to manage credential request data and state
 */
export function useCredentialRequest(): UseCredentialRequestReturn {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const isNewRequest = id === 'new';
  
  const [request, setRequest] = useState<CredentialRequest | null>(null);
  const [ministers, setMinisters] = useState<Minister[]>([]);
  // For new requests, start with isLoading = false since there's nothing to load initially
  // Check path directly since id from useParams might not be available during initialization
  const [isLoading, setIsLoading] = useState(() => {
    // If the path ends with '/new', it's a new request - no loading needed
    return !window.location.pathname.endsWith('/new');
  });
  const [error, setError] = useState<string | null>(null);
  const [districtName, setDistrictName] = useState<string>("");
  const [regionName, setRegionName] = useState<string>("");

  // Detect credential type from loaded request or URL path
  const isInstitutionRequest = request?.credential_type === 'INSTITUTION';
  const isFunctionRequest = location.pathname.includes('/function');
  const isNacionalFunctionRequest =
    request?.credential_type === 'FUNCTION' &&
    !request?.district_id &&
    !request?.region_id &&
    !!request?.institution_id;

  const districtIdFromUrl = searchParams.get('districtId');
  const regionIdFromUrl = searchParams.get('regionId');
  const yearFromUrl = searchParams.get('year') 
    ? parseInt(searchParams.get('year')!) 
    : new Date().getFullYear();

  const canEdit = request ? canEditRequest(request.status) : isNewRequest;

  const urlParams: RequestUrlParams = {
    districtId: districtIdFromUrl,
    regionId: regionIdFromUrl,
    year: yearFromUrl,
  };

  const loadRequest = async () => {
    if (isNewRequest) {
      setIsLoading(true);
      try {
        if (districtIdFromUrl && regionIdFromUrl) {
          const [district, region] = await Promise.all([
            getDistrictById(districtIdFromUrl),
            getRegionById(regionIdFromUrl),
          ]);
          if (district) setDistrictName(district.name || "");
          if (region) setRegionName(region.name || "");
        }
      } catch (err) {
        console.error("Error loading district/region:", err);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!id) return;

    setIsLoading(true);
    try {
      const data = await getRequestById(id);
      if (!data) {
        setError("Solicitud no encontrada");
        return;
      }
      setRequest(data);

      const ministersData = await getCredentialReqMinisters(id);
      setMinisters(ministersData);
    } catch (err) {
      console.error("Error loading request:", err);
      setError("Error al cargar la solicitud");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNewRequest, districtIdFromUrl, regionIdFromUrl]);

  return {
    request,
    ministers,
    districtName,
    regionName,
    urlParams,
    isLoading,
    error,
    isNewRequest,
    canEdit,
    isInstitutionRequest,
    isFunctionRequest,
    isNacionalFunctionRequest,
    setRequest,
    setMinisters,
    setError,
    reloadRequest: loadRequest,
  };
}
